/**
 * Pure filter / summary math used by <InvoiceHistorySection>.
 *
 * Extracted so the invariant "superseded invoices don't inflate parent
 * totals" and "outstanding-only excludes zero-balance rows" can be unit
 * tested without rendering the whole Redux-backed component.
 *
 * `InvoiceRow` mirrors the shape returned by GET /api/parent/{id}/invoices.
 */

export type InvoiceRow = {
  id: number;
  invoice_number?: string | null;
  student_id?: number | null;
  status?: string | null;
  is_overdue?: boolean;
  total_amount?: number | string | null;
  paid_amount?: number | string | null;
  balance_due?: number | string | null;
};

export type FilterState = {
  studentFilter?: string;
  statusFilter?: string;
  outstandingOnly?: boolean;
  search?: string;
};

export function filterParentInvoices(invoices: InvoiceRow[], f: FilterState): InvoiceRow[] {
  return invoices.filter((inv) => {
    if (f.studentFilter && String(inv.student_id) !== f.studentFilter) return false;
    if (f.statusFilter) {
      if (f.statusFilter === "overdue") {
        if (!inv.is_overdue) return false;
      } else if (String(inv.status).toLowerCase() !== f.statusFilter) return false;
    }
    if (f.outstandingOnly && Number(inv.balance_due ?? 0) <= 0) return false;
    if (f.search && f.search.trim()) {
      const q = f.search.trim().toLowerCase();
      if (!String(inv.invoice_number ?? "").toLowerCase().includes(q)) return false;
    }
    return true;
  });
}

export type ParentInvoiceSummary = {
  total_invoiced: number;
  total_paid: number;
  outstanding: number;
  overdue: number;
  invoice_count: number;
};

export function summariseParentInvoices(invoices: InvoiceRow[]): ParentInvoiceSummary {
  let total = 0, paid = 0, overdue = 0;
  invoices.forEach((i) => {
    total += Number(i.total_amount ?? 0);
    paid += Number(i.paid_amount ?? 0);
    if (i.is_overdue) overdue += Math.max(0, Number(i.balance_due ?? 0));
  });
  return {
    total_invoiced: total,
    total_paid: paid,
    outstanding: Math.max(0, total - paid),
    overdue,
    invoice_count: invoices.length,
  };
}
